import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';

const LoginWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%);
  padding: var(--spacing-4);
`;

const LoginCard = styled.div`
  background: white;
  padding: var(--spacing-8);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-lg);
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const Logo = styled.div`
  font-size: var(--font-size-2xl);
  font-weight: 600;
  color: var(--color-secondary);
  margin-bottom: var(--spacing-2);
`;

const Subtitle = styled.p`
  color: var(--color-text-light);
  margin-bottom: var(--spacing-8);
  font-size: var(--font-size-sm);
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
`;

const FormGroup = styled.div`
  text-align: left;
  
  label {
    display: block;
    margin-bottom: var(--spacing-2);
    font-weight: 500;
    color: var(--color-text);
    font-size: var(--font-size-sm);
  }
  
  input {
    width: 100%;
    padding: var(--spacing-3);
    border: 1px solid var(--color-border);
    border-radius: var(--border-radius);
    font-size: var(--font-size-base);
    transition: border-color 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: var(--color-secondary);
    }
    
    &:disabled {
      background-color: #f5f5f5;
      cursor: not-allowed;
    }
  }
`;

const LoginButton = styled.button`
  background-color: var(--color-secondary);
  color: white;
  padding: var(--spacing-3) var(--spacing-4);
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-base);
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-top: var(--spacing-4);
  
  &:hover:not(:disabled) {
    background-color: #1f1e3a;
  }
  
  &:disabled {
    background-color: var(--color-text-light);
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background-color: #fee2e2;
  color: #dc2626;
  padding: var(--spacing-3);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  margin-bottom: var(--spacing-4);
  text-align: left;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: var(--spacing-2);
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const BackToSite = styled.a`
  display: inline-block;
  margin-top: var(--spacing-6);
  color: var(--color-text-light);
  text-decoration: none;
  font-size: var(--font-size-sm);
  
  &:hover {
    color: var(--color-secondary);
    text-decoration: underline;
  }
`;


const AdminLogin: React.FC = () => {
  const { login, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Redirect if already authenticated
  if (isAuthenticated) {
    const from = (location.state as any)?.from?.pathname || '/admin';
    return <Navigate to={from} replace />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const success = await login(formData.username, formData.password);
    
    if (!success) {
      setError('Felaktigt användarnamn eller lösenord. Försök igen.');
      setIsLoading(false);
    }
    // If successful, the redirect will happen automatically via the auth context
  };

  if (loading) {
    return (
      <LoginWrapper>
        <LoginCard>
          <div style={{ padding: 'var(--spacing-8)' }}>
            <LoadingSpinner />
            Laddar...
          </div>
        </LoginCard>
      </LoginWrapper>
    );
  }

  return (
    <LoginWrapper>
      <LoginCard>
        <Logo>🌸 Lillaflora Admin</Logo>
        <Subtitle>Logga in för att komma åt kontrollpanelen</Subtitle>
        

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <LoginForm onSubmit={handleSubmit}>
          <FormGroup>
            <label htmlFor="username">Användarnamn</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              disabled={isLoading}
              required
              autoComplete="username"
            />
          </FormGroup>

          <FormGroup>
            <label htmlFor="password">Lösenord</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              disabled={isLoading}
              required
              autoComplete="current-password"
            />
          </FormGroup>

          <LoginButton type="submit" disabled={isLoading || !formData.username || !formData.password}>
            {isLoading && <LoadingSpinner />}
            {isLoading ? 'Loggar in...' : 'Logga in'}
          </LoginButton>
        </LoginForm>

        <BackToSite href="/">
          ← Tillbaka till Lillaflora
        </BackToSite>
      </LoginCard>
    </LoginWrapper>
  );
};

export default AdminLogin;